import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

/**
 * Dropdown Hover Controller
 *
 * Triggers dropdown clicks on hover to leverage Bootstrap's Popper.js positioning
 * instead of CSS hover states that can interfere with positioning.
 *
 * Usage:
 * - Add `data-controller="dropdown-hover"` to dropdown container
 * - Add `data-dropdown-hover-target="toggle"` to dropdown toggle
 * - Add `data-dropdown-hover-target="menu"` to dropdown menu
 */
export default class extends Controller {
  static targets = ["toggle", "menu"]

  connect() {
    this.setupResponsiveBehavior()
    this.setupHoverEvents()
    this.setupClickNavigation()
  }

  disconnect() {
    this.removeHoverEvents()
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
  }

  setupResponsiveBehavior() {
    this.resizeListener = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeListener)
    this.handleResize()
  }

  handleResize() {
    if (this.isMobile()) {
      this.removeHoverEvents()
    } else {
      this.setupHoverEvents()
    }
  }

  isMobile() {
    return window.innerWidth < 992 // Bootstrap lg breakpoint
  }

  setupHoverEvents() {
    if (this.hasToggleTarget && this.hasMenuTarget && !this.isMobile()) {
      this.toggleTarget.addEventListener('mouseenter', this.showDropdown.bind(this))
      this.menuTarget.addEventListener('mouseenter', this.cancelHide.bind(this))
      this.menuTarget.addEventListener('mouseleave', this.hideDropdownWithDelay.bind(this))
      this.element.addEventListener('mouseleave', this.hideDropdownWithDelay.bind(this))
    }
  }

  removeHoverEvents() {
    if (this.hasToggleTarget && this.hasMenuTarget) {
      this.toggleTarget.removeEventListener('mouseenter', this.showDropdown.bind(this))
      this.menuTarget.removeEventListener('mouseenter', this.cancelHide.bind(this))
      this.menuTarget.removeEventListener('mouseleave', this.hideDropdownWithDelay.bind(this))
      this.element.removeEventListener('mouseleave', this.hideDropdownWithDelay.bind(this))
    }
    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
  }

  showDropdown() {
    if (!this.isMobile()) {
      // Check if dropdown is already showing
      const existingDropdown = Dropdown.getInstance(this.toggleTarget)
      if (existingDropdown && this.toggleTarget.getAttribute('aria-expanded') === 'true') {
        // Dropdown is already open, just cancel any pending hide
        this.cancelHide()
        return
      }

      // Trigger Bootstrap's dropdown show method
      const dropdown = new Dropdown(this.toggleTarget)
      dropdown.show()
    }
  }

  hideDropdown() {
    if (!this.isMobile()) {
      // Trigger Bootstrap's dropdown hide method
      const dropdown = Dropdown.getInstance(this.toggleTarget)
      if (dropdown) {
        dropdown.hide()
      }
    }
  }

  hideDropdownWithDelay() {
    if (!this.isMobile()) {
      // Add a small delay to prevent accidental closing when moving mouse to dropdown
      clearTimeout(this.hideTimeout)
      this.hideTimeout = setTimeout(() => {
        this.hideDropdown()
      }, 150) // 150ms delay
    }
  }

  cancelHide() {
    // Cancel any pending hide operation when mouse enters the dropdown menu
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  setupClickNavigation() {
    // Allow click to navigate to the link's href instead of just toggling dropdown
    if (this.hasToggleTarget) {
      this.toggleTarget.addEventListener('click', (event) => {
        // Only navigate if the toggle is an actual link with href (not just "#")
        const href = this.toggleTarget.getAttribute('href')
        if (href && href !== '#' && href !== '') {
          // Prevent Bootstrap's dropdown toggle on click
          event.stopPropagation()
          // Let Turbo handle the navigation
          window.Turbo.visit(href, { frame: "_top" })
          return false
        }
        // For links with href="#", let Bootstrap handle the dropdown toggle normally
      }, true) // Use capture phase to run before Bootstrap
    }
  }
}
